/**
 * useTimeRequestAlerts - Real-time listener for "request more time" alerts
 *
 * Subscribes to time_request_alerts/ collection in Firestore
 * and returns active (not cleared) alerts. Used by office/ops
 * dashboards to show real-time time extension requests from techs.
 */

import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';
import { TimeRequestAlert } from '@/types/schema';

interface UseTimeRequestAlertsReturn {
  /** All active (not cleared) time request alerts */
  alerts: TimeRequestAlert[];
  /** Number of unacknowledged time request alerts */
  unacknowledgedCount: number;
  /** Whether the listener is still loading */
  loading: boolean;
}

/**
 * Subscribe to real-time time request alerts for a tenant.
 */
export function useTimeRequestAlerts(tenantId: string | null): UseTimeRequestAlertsReturn {
  const [alerts, setAlerts] = useState<TimeRequestAlert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) {
      setAlerts([]);
      setLoading(false);
      return;
    }

    // Query most-recent alerts for tenant and filter cleared ones client-side.
    // Firestore where('field', '==', null) is unreliable for documents where
    // the field is absent vs explicitly null, so we filter in-memory instead.
    // Capped at 100 most-recent — same cost-ceiling rationale as useSOSAlerts.
    const q = query(
      collection(db, 'time_request_alerts'),
      where('tenantId', '==', tenantId),
      orderBy('requestedAt', 'desc'),
      limit(100),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs
          .map((d) => ({
            id: d.id,
            ...d.data(),
          })) as TimeRequestAlert[];

        // Filter out cleared alerts client-side for reliability
        const active = data.filter((a) => !a.clearedAt);

        setAlerts(active);
        setLoading(false);
      },
      (err) => {
        console.error('[useTimeRequestAlerts] Listener error:', err.message);
        setLoading(false);
      },
    );

    return unsubscribe;
  }, [tenantId]);

  const unacknowledgedCount = alerts.filter((a) => !a.acknowledged).length;

  return { alerts, unacknowledgedCount, loading };
}
